import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Space, Spin, TimePicker, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { GAME_PROFILE } from '../../../constants/define/translation';
import { ZONE } from '../../../constants/common';
import { DragDropImageGameProfile } from '../../molecules/upload';
import { CONFIG_CONTAINER_NAME, UploadFileToBlobInterface, uploadFileToBlob } from '../../../utils/azure';
import { CreateGameProfileQuestionSetPayload, GameProfileQuestionSet } from '../../../types/gameProfileQuestionSet';
import {
  createGameProfileQuestionSetRepository,
  getGameProfileQuestionSetDetailRepository,
  updateGameProfileQuestionSetRepository,
} from '../../../repository/mytm/gameProfile/gameProfileQuestionSetsRepository';
import { getGameProfilesRepository } from '../../../repository/mytm/gameProfile/gameProfileRepository';
import { PreviewEntryCard } from '../../molecules/preview/common/PreviewEntryCard';
import { logging } from '../../../utils/log';

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

interface QuestionSetModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  questionSetId?: string | null;
  country?: string | null;
}

export const QuestionSetModal: React.FC<QuestionSetModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  questionSetId,
  country,
}) => {
  const { t } = useTranslation(GAME_PROFILE);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [entryExpanded, setEntryExpanded] = useState(false);
  const [questionnaireExpanded, setQuestionnaireExpanded] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  // Store default data from API
  const [defaultEntryCardData, setDefaultEntryCardData] = useState<any>(null);
  const [defaultQuestionnaireData, setDefaultQuestionnaireData] = useState<any>(null);
  // Question set data for edit mode
  const [questionSetData, setQuestionSetData] = useState<GameProfileQuestionSet | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  // Date/Time validation state
  const [selectedStartTime, setSelectedStartTime] = useState<any>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<any>(null);
  const [dateTimeValidation, setDateTimeValidation] = useState<{
    validateStatus?: 'error' | 'success';
    help?: string;
    isCheckStartDate?: boolean;
  }>({});
  // Character count states for real-time counters
  const [setNameCharCount, setSetNameCharCount] = useState(0);
  const [entryHeaderCharCount, setEntryHeaderCharCount] = useState(0);
  const [entrySubHeaderCharCount, setEntrySubHeaderCharCount] = useState(0);
  const [entryButtonTextCharCount, setEntryButtonTextCharCount] = useState(0);
  const [questionnaireHeaderCharCount, setQuestionnaireHeaderCharCount] = useState(0);
  const [questionnaireSubHeaderCharCount, setQuestionnaireSubHeaderCharCount] = useState(0);
  const [questionnaireHeaderColorCharCount, setQuestionnaireHeaderColorCharCount] = useState(0);
  const [questionnaireSubHeaderColorCharCount, setQuestionnaireSubHeaderColorCharCount] = useState(0);
  const [thankYouHeaderCharCount, setThankYouHeaderCharCount] = useState(0);

  // Fetch question set data when in edit mode
  useEffect(() => {
    const fetchQuestionSetData = async () => {
      if (!questionSetId) {
        setQuestionSetData(null);
        return;
      }

      try {
        setIsLoadingData(true);
        const data = await getGameProfileQuestionSetDetailRepository(questionSetId);
        setQuestionSetData(data);
      } catch (error) {
        logging(error);
        message.error(t('questionSetModal.messages.loadFailed'));
        setQuestionSetData(null);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (visible) {
      fetchQuestionSetData();
    }
  }, [questionSetId, visible]);

  // Load default data or question set data when modal opens
  useEffect(() => {
    if (visible) {
      const loadData = async () => {
        // If editing, populate with question set data
        if (questionSetData) {
          const options = questionSetData.options ? JSON.parse(questionSetData.options) : {};
          const startDateTime = questionSetData.startTime ? moment(questionSetData.startTime).tz(ZONE) : null;
          const endDateTime = questionSetData.endTime ? moment(questionSetData.endTime).tz(ZONE) : null;

          form.setFieldsValue({
            name: questionSetData.groupSetName,
            startDate: startDateTime,
            startTime: startDateTime,
            endDate: endDateTime,
            endTime: endDateTime,
            entryHeader: questionSetData.header || '',
            entrySubHeader: questionSetData.subHeader || '',
            entryButtonText: questionSetData.ctaButton || '',
            entryBackgroundColor: questionSetData.backgroundColor || '',
            annexActionUsaId: questionSetData.annexActionUsaId || '',
            annexActionCanId: questionSetData.annexActionCanId || '',
            questionnaireHeader: questionSetData.questionHeader || '',
            questionnaireSubHeader: questionSetData.questionSubHeader || '',
            questionnaireHeaderColor: options.headerColor || '#7b7b7b',
            questionnaireSubHeaderColor: options.subHeaderColor || '#127603',
            questionnaireThankYouHeader: questionSetData.thankYouHeader || '',
            questionnaireThankYouMessage: questionSetData.thankYouMessage || '',
          });

          // Set selected times for validation
          setSelectedStartTime(startDateTime);
          setSelectedEndTime(endDateTime);
          // Set character counts
          setSetNameCharCount(questionSetData.groupSetName?.length || 0);
          setEntryHeaderCharCount(questionSetData.header?.length || 0);
          setEntrySubHeaderCharCount(questionSetData.subHeader?.length || 0);
          setEntryButtonTextCharCount(questionSetData.ctaButton?.length || 0);
          setQuestionnaireHeaderCharCount(questionSetData.questionHeader?.length || 0);
          setQuestionnaireSubHeaderCharCount(questionSetData.questionSubHeader?.length || 0);
          setQuestionnaireHeaderColorCharCount(options.headerColor?.length || 0);
          setQuestionnaireSubHeaderColorCharCount(options.subHeaderColor?.length || 0);
          setThankYouHeaderCharCount(questionSetData.thankYouHeader?.length || 0);

          if (questionSetData.imageLink) {
            setImageList([{ dataURL: questionSetData.imageLink, isImageURL: true }]);
          }
        } else {
          // Load default data for create mode
          try {
            const response = await getGameProfilesRepository(country);
            const entryCardData = response.gameProfiles.find(item => item.type === 'ENTRY_CARD');
            if (entryCardData) {
              setDefaultEntryCardData(entryCardData);
              form.setFieldsValue({
                entryHeader: entryCardData.header || '',
                entrySubHeader: entryCardData.subHeader || '',
                entryButtonText: entryCardData.ctaButton || '',
                entryBackgroundColor: entryCardData.backgroundColor || '',
                annexActionUsaId: entryCardData.annexActionUsaId || '',
                annexActionCanId: entryCardData.annexActionCanId || '',
              });
              if (entryCardData.imageLink) {
                setImageList([{ dataURL: entryCardData.imageLink, isImageURL: true }]);
              }
            }
            const questionnaireData = response.gameProfiles.find(item => item.type === 'QUESTIONNAIRE');
            if (questionnaireData) {
              setDefaultQuestionnaireData(questionnaireData);
              const options = questionnaireData.options ? JSON.parse(questionnaireData.options) : {};
              form.setFieldsValue({
                questionnaireHeader: questionnaireData.header || '',
                questionnaireSubHeader: questionnaireData.subHeader || '',
                questionnaireHeaderColor: options.headerColor || '#7b7b7b',
                questionnaireSubHeaderColor: options.subHeaderColor || '#127603',
                questionnaireThankYouHeader: questionnaireData.thankYouHeader || '',
                questionnaireThankYouMessage: questionnaireData.thankYouMessage || '',
              });
            }
          } catch (error) {
            // Intentionally ignore errors when loading default data - form can still function without it
          }
        }
      };

      form.resetFields();
      setEntryExpanded(false);
      setQuestionnaireExpanded(false);
      setImageList([]);
      setSelectedImage(0);
      setSelectedStartTime(null);
      setSelectedEndTime(null);
      setDateTimeValidation({});
      setSetNameCharCount(0);
      setEntryHeaderCharCount(0);
      setEntrySubHeaderCharCount(0);
      setEntryButtonTextCharCount(0);
      setQuestionnaireHeaderCharCount(0);
      setQuestionnaireSubHeaderCharCount(0);
      setQuestionnaireHeaderColorCharCount(0);
      setQuestionnaireSubHeaderColorCharCount(0);
      setThankYouHeaderCharCount(0);
      loadData();
    }
  }, [visible, questionSetData, form]);

  const buildDateTime = (dateValue: any, timeValue: any) => {
    if (!dateValue || !timeValue) return null;
    const date = moment.isMoment(dateValue) ? dateValue : moment(dateValue);
    const time = moment.isMoment(timeValue) ? timeValue : moment(timeValue);
    return date.tz(ZONE).set({
      hour: time.hour(),
      minute: time.minute(),
      second: 0,
      millisecond: 0,
    });
  };

  // Validate date and time selection
  const validateDateTime = (startTimeVal: any, endTimeVal: any) => {
    const startDateVal = form.getFieldValue('startDate');
    const endDateVal = form.getFieldValue('endDate');

    let validation: {
      validateStatus?: 'error' | 'success';
      help?: string;
      isCheckStartDate?: boolean;
    } = {
      validateStatus: undefined,
      help: undefined,
      isCheckStartDate: undefined,
    };

    if (startDateVal && endDateVal && startTimeVal && endTimeVal) {
      const startDateTime = buildDateTime(startDateVal, startTimeVal);
      const endDateTime = buildDateTime(endDateVal, endTimeVal);

      if (startDateTime && endDateTime && startDateTime.isAfter(endDateTime)) {
        validation = {
          validateStatus: 'error',
          help: t('questionSetModal.validation.endAfterStart'),
          isCheckStartDate: true,
        };
      } else {
        validation = {
          validateStatus: 'success',
          help: undefined,
          isCheckStartDate: true,
        };
      }
    }

    setDateTimeValidation(validation);
  };

  const handleStartTimeChange = (value: any) => {
    setSelectedStartTime(value);
    validateDateTime(value, selectedEndTime);
  };

  const handleEndTimeChange = (value: any) => {
    setSelectedEndTime(value);
    validateDateTime(selectedStartTime, value);
  };

  const handleDateChange = () => {
    validateDateTime(selectedStartTime, selectedEndTime);
  };

  // Disable end dates before start date
  const disabledEndDate = (current: any) => {
    if (!current) return false;

    const startDateVal = form.getFieldValue('startDate');
    const today = moment().startOf('day');

    // Disable dates before today
    if (current < today) {
      return true;
    }

    // If start date is selected, disable dates before start date
    if (startDateVal) {
      const startDate = moment(startDateVal).startOf('day');
      return current < startDate;
    }

    return false;
  };

  const handleUploadBackground = async (): Promise<string | null> => {
    if (!imageList.length) return null;
    const newImages = imageList.filter(img => !img.isImageURL);
    const existingImages = imageList.filter(img => img.isImageURL);

    if (!newImages.length) {
      const [firstExisting] = existingImages;
      return firstExisting?.dataURL || null;
    }

    const uploadedImages = await uploadFileToBlob(newImages, CONFIG_CONTAINER_NAME.UPLOADS);
    let imageUrls = uploadedImages;
    if (azureCDN) {
      imageUrls = uploadedImages.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
    }
    const [firstNew] = imageUrls;
    return firstNew || null;
  };

  const validateSchedule = (startDate: any, startTime: any, endDate: any, endTime: any) => {
    // If all fields are empty, schedule is optional - return null to skip schedule
    if (!startDate && !startTime && !endDate && !endTime) {
      return { start: null, end: null };
    }

    // If some fields are filled but not all, show error
    if (!startDate || !startTime || !endDate || !endTime) {
      message.error(t('questionSetModal.validation.scheduleRequired'));
      return null;
    }

    const start = buildDateTime(startDate, startTime);
    const end = buildDateTime(endDate, endTime);

    if (!start || !end) {
      message.error(t('questionSetModal.validation.scheduleRequired'));
      return null;
    }

    if (!start.isBefore(end)) {
      message.error(t('questionSetModal.validation.endAfterStart'));
      return null;
    }

    return { start, end };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const schedule = validateSchedule(values.startDate, values.startTime, values.endDate, values.endTime);
      if (!schedule) return;

      setLoading(true);

      let backgroundImage: string | null = null;
      try {
        backgroundImage = await handleUploadBackground();
      } catch (error) {
        message.error(t('questionSetModal.messages.uploadFailed'));
        setLoading(false);
        return;
      }

      // Edit mode: use questionSetData (original data) when sections are not expanded
      // Create mode: use form values if expanded, otherwise use default data
      let entryData;
      if (questionSetId && !entryExpanded) {
        // Edit mode without expansion: preserve original data
        entryData = {
          header: questionSetData?.header || '',
          subHeader: questionSetData?.subHeader || '',
          ctaButton: questionSetData?.ctaButton || '',
          backgroundColor: questionSetData?.backgroundColor || '',
          annexActionUsaId: questionSetData?.annexActionUsaId || '',
          annexActionCanId: questionSetData?.annexActionCanId || '',
        };
      } else if (entryExpanded) {
        // Expanded: use form values (works for both create and edit)
        entryData = {
          header: values.entryHeader || '',
          subHeader: values.entrySubHeader || '',
          ctaButton: values.entryButtonText || '',
          backgroundColor: values.entryBackgroundColor || '',
          annexActionUsaId: values.annexActionUsaId || '',
          annexActionCanId: values.annexActionCanId || '',
        };
      } else {
        // Create mode without expansion: use default data
        entryData = {
          header: defaultEntryCardData?.header || '',
          subHeader: defaultEntryCardData?.subHeader || '',
          ctaButton: defaultEntryCardData?.ctaButton || '',
          backgroundColor: defaultEntryCardData?.backgroundColor || '',
          annexActionUsaId: defaultEntryCardData?.annexActionUsaId || '',
          annexActionCanId: defaultEntryCardData?.annexActionCanId || '',
        };
      }

      let questionnaireData;
      if (questionSetId && !questionnaireExpanded) {
        // Edit mode without expansion: preserve original data
        const options = questionSetData?.options ? JSON.parse(questionSetData.options) : {};
        questionnaireData = {
          questionHeader: questionSetData?.questionHeader || '',
          questionSubHeader: questionSetData?.questionSubHeader || '',
          headerColor: options.headerColor || '',
          subHeaderColor: options.subHeaderColor || '',
          thankYouHeader: questionSetData?.thankYouHeader || '',
          thankYouMessage: questionSetData?.thankYouMessage || '',
        };
      } else if (questionnaireExpanded) {
        // Expanded: use form values (works for both create and edit)
        questionnaireData = {
          questionHeader: values.questionnaireHeader || '',
          questionSubHeader: values.questionnaireSubHeader || '',
          headerColor: values.questionnaireHeaderColor || '',
          subHeaderColor: values.questionnaireSubHeaderColor || '',
          thankYouHeader: values.questionnaireThankYouHeader || '',
          thankYouMessage: values.questionnaireThankYouMessage || '',
        };
      } else {
        // Create mode without expansion: use default data
        questionnaireData = {
          questionHeader: defaultQuestionnaireData?.header || '',
          questionSubHeader: defaultQuestionnaireData?.subHeader || '',
          headerColor: defaultQuestionnaireData?.options
            ? JSON.parse(defaultQuestionnaireData.options).headerColor || ''
            : '',
          subHeaderColor: defaultQuestionnaireData?.options
            ? JSON.parse(defaultQuestionnaireData.options).subHeaderColor || ''
            : '',
          thankYouHeader: defaultQuestionnaireData?.thankYouHeader || '',
          thankYouMessage: defaultQuestionnaireData?.thankYouMessage || '',
        };
      }

      const payload: CreateGameProfileQuestionSetPayload = {
        groupSetName: values.name.trim(),
        startTime: schedule.start ? schedule.start.toISOString() : null,
        endTime: schedule.end ? schedule.end.toISOString() : null,
        header: entryData.header,
        subHeader: entryData.subHeader,
        ctaButton: entryData.ctaButton,
        backgroundColor: entryData.backgroundColor,
        annexActionUsaId: entryData.annexActionUsaId,
        annexActionCanId: entryData.annexActionCanId,
        imageLink: backgroundImage || defaultEntryCardData?.imageLink || '',
        questionHeader: questionnaireData.questionHeader,
        questionSubHeader: questionnaireData.questionSubHeader,
        options: JSON.stringify({
          headerColor: questionnaireData.headerColor,
          subHeaderColor: questionnaireData.subHeaderColor,
        }),
        thankYouHeader: questionnaireData.thankYouHeader,
        thankYouMessage: questionnaireData.thankYouMessage,
        type: null,
        disabled: questionSetData?.disabled ?? false,
        country: country || undefined,
      };

      if (questionSetId) {
        // Update existing question set
        await updateGameProfileQuestionSetRepository(questionSetId, payload);
        message.success(t('questionSetModal.messages.updated'));
      } else {
        // Create new question set
        await createGameProfileQuestionSetRepository(payload);
        message.success(t('questionSetModal.messages.created'));
      }
      onSuccess(); // Notify parent to reload question sets
      onCancel(); // Close modal
      form.resetFields();
      setEntryExpanded(false);
      setQuestionnaireExpanded(false);
      setImageList([]);
      setSelectedImage(0);
    } catch (error: any) {
      const apiError = error?.errorMessage || error?.message;
      message.error(apiError || t('questionSetModal.messages.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEntryCard = () => {
    // Only reset to default values when adding new question set (not editing)
    if (!questionSetId && defaultEntryCardData) {
      form.setFieldsValue({
        entryHeader: defaultEntryCardData.header || '',
        entrySubHeader: defaultEntryCardData.subHeader || '',
        entryButtonText: defaultEntryCardData.ctaButton || '',
        entryBackgroundColor: defaultEntryCardData.backgroundColor || '',
        annexActionUsaId: defaultEntryCardData.annexActionUsaId || '',
        annexActionCanId: defaultEntryCardData.annexActionCanId || '',
      });
      // Reset image to default
      if (defaultEntryCardData.imageLink) {
        setImageList([{ dataURL: defaultEntryCardData.imageLink, isImageURL: true }]);
      } else {
        setImageList([]);
      }
    }
    setEntryExpanded(false);
  };

  const handleCancelQuestionnaire = () => {
    // Only reset to default values when adding new question set (not editing)
    if (!questionSetId && defaultQuestionnaireData) {
      const options = defaultQuestionnaireData.options ? JSON.parse(defaultQuestionnaireData.options) : {};
      form.setFieldsValue({
        questionnaireHeader: defaultQuestionnaireData.header || '',
        questionnaireSubHeader: defaultQuestionnaireData.subHeader || '',
        questionnaireHeaderColor: options.headerColor || '#7b7b7b',
        questionnaireSubHeaderColor: options.subHeaderColor || '#127603',
        questionnaireThankYouHeader: defaultQuestionnaireData.thankYouHeader || '',
        questionnaireThankYouMessage: defaultQuestionnaireData.thankYouMessage || '',
      });
    }
    setQuestionnaireExpanded(false);
  };

  return (
    <StyledModalWrapper>
      <Global
        styles={css`
          .question-set-modal-wrapper .ant-modal-content {
            border-radius: 10px;
            overflow: hidden;
          }
          .question-set-modal-wrapper textarea.ant-input {
            border-radius: 6px !important;
          }
        `}
      />
      <Modal
        title={questionSetId ? t('questionSetModal.editTitle') : t('questionSetModal.title')}
        confirmLoading={loading || isLoadingData}
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={960}
        destroyOnClose
        maskClosable={false}
        zIndex={5} // ensure this modal stacks above the parent Question modal
        wrapClassName="question-set-modal-wrapper"
      >
        <StyledFormWrapper>
          <Spin spinning={isLoadingData}>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.name')}</span>}
                    rules={[{ required: true, message: t('questionSetModal.validation.name') }]}
                  >
                    <Input
                      placeholder={t('questionSetModal.placeholders.name')}
                      style={{ borderRadius: 6 }}
                      maxLength={100}
                      suffix={<span style={{ color: '#bfbfbf', fontSize: '14px' }}>{setNameCharCount}/100</span>}
                      onChange={e => {
                        setSetNameCharCount(e.target.value.length);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="startTime"
                    label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.startTime')}</span>}
                    validateStatus={dateTimeValidation.isCheckStartDate ? dateTimeValidation.validateStatus : undefined}
                    help={dateTimeValidation.isCheckStartDate ? dateTimeValidation.help : undefined}
                  >
                    <TimePicker
                      use12Hours
                      format="h:mm A"
                      placeholder="12:00 AM"
                      style={{ width: '100%', borderRadius: 6 }}
                      value={selectedStartTime}
                      onChange={handleStartTimeChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="startDate"
                    label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.startDate')}</span>}
                    validateStatus={dateTimeValidation.isCheckStartDate ? dateTimeValidation.validateStatus : undefined}
                  >
                    <DatePicker
                      format="MM/DD/YYYY"
                      placeholder="MM/DD/YYYY"
                      style={{ width: '100%', borderRadius: 6 }}
                      disabledDate={current => current && current < moment().startOf('day')}
                      onChange={handleDateChange}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="endTime"
                    label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.endTime')}</span>}
                    validateStatus={
                      !dateTimeValidation.isCheckStartDate ? dateTimeValidation.validateStatus : undefined
                    }
                    help={!dateTimeValidation.isCheckStartDate ? dateTimeValidation.help : undefined}
                  >
                    <TimePicker
                      use12Hours
                      format="h:mm A"
                      placeholder="12:00 AM"
                      style={{ width: '100%', borderRadius: 6 }}
                      value={selectedEndTime}
                      onChange={handleEndTimeChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="endDate"
                    label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.endDate')}</span>}
                    validateStatus={
                      !dateTimeValidation.isCheckStartDate ? dateTimeValidation.validateStatus : undefined
                    }
                  >
                    <DatePicker
                      format="MM/DD/YYYY"
                      placeholder="MM/DD/YYYY"
                      style={{ width: '100%', borderRadius: 6 }}
                      disabledDate={disabledEndDate}
                      onChange={handleDateChange}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {entryExpanded && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      marginTop: 16,
                    }}
                  >
                    <StyledSectionTitle>{t('questionSetModal.labels.entryCardSection')}</StyledSectionTitle>
                    <StyledCancelButton type="link" onClick={handleCancelEntryCard}>
                      {t('questionSetModal.buttons.cancel')}
                    </StyledCancelButton>
                  </div>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="entryHeader"
                        label={<span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.entryHeader')}</span>}
                        rules={[{ required: true, message: t('questionSetModal.validation.entryHeader') }]}
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.entryHeader')}
                          style={{ borderRadius: 6 }}
                          maxLength={50}
                          suffix={<span style={{ color: '#bfbfbf', fontSize: '14px' }}>{entryHeaderCharCount}/50</span>}
                          onChange={e => setEntryHeaderCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="entrySubHeader"
                        label={
                          <span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.entrySubHeader')}</span>
                        }
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.entrySubHeader')}
                          style={{ borderRadius: 6 }}
                          maxLength={120}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>{entrySubHeaderCharCount}/120</span>
                          }
                          onChange={e => setEntrySubHeaderCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="entryButtonText"
                        label={
                          <span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.entryButtonText')}</span>
                        }
                        rules={[{ required: true, message: t('questionSetModal.validation.entryButtonText') }]}
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.entryButtonText')}
                          style={{ borderRadius: 6 }}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>{entryButtonTextCharCount}/50</span>
                          }
                          onChange={e => setEntryButtonTextCharCount(e.target.value.length)}
                          maxLength={50}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="entryBackgroundColor"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.entryBackgroundColor')}
                          </span>
                        }
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.entryBackgroundColor')}
                          style={{ borderRadius: 6 }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    {(!country || country === 'USA') && (
                      <Col span={12}>
                        <Form.Item
                          name="annexActionUsaId"
                          label={
                            <span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.annexActionUsaId')}</span>
                          }
                        >
                          <Input
                            placeholder={t('questionSetModal.placeholders.annexActionUsaId')}
                            style={{ borderRadius: 6 }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {(!country || country === 'CAN') && (
                      <Col span={12}>
                        <Form.Item
                          name="annexActionCanId"
                          label={
                            <span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.annexActionCanId')}</span>
                          }
                        >
                          <Input
                            placeholder={t('questionSetModal.placeholders.annexActionCanId')}
                            style={{ borderRadius: 6 }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ height: 310, backgroundColor: '#BFBFBF', borderRadius: 8 }}>
                        <DragDropImageGameProfile
                          imageList={imageList}
                          onSetImageList={setImageList}
                          selectedImage={selectedImage}
                          onSelectedImage={setSelectedImage}
                          maxImage={1}
                          multipleUpload={false}
                          nodePreview={
                            <PreviewEntryCard
                              values={{
                                header: form.getFieldValue('entryHeader'),
                                subHeader: form.getFieldValue('entrySubHeader'),
                                ctaButton: form.getFieldValue('entryButtonText'),
                                backgroundImage: imageList?.length ? imageList[0]?.dataURL : null,
                              }}
                              isPreview
                              height="200px"
                            />
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                </>
              )}

              {questionnaireExpanded && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      marginTop: 16,
                    }}
                  >
                    <StyledSectionTitle>{t('questionSetModal.labels.questionnaireSection')}</StyledSectionTitle>
                    <StyledCancelButton type="link" onClick={handleCancelQuestionnaire}>
                      {t('questionSetModal.buttons.cancel')}
                    </StyledCancelButton>
                  </div>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireHeader"
                        label={
                          <span style={{ fontWeight: 'bold' }}>{t('questionSetModal.labels.questionnaireHeader')}</span>
                        }
                        rules={[{ required: true, message: t('questionSetModal.validation.questionnaireHeader') }]}
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.questionnaireHeader')}
                          style={{ borderRadius: 6 }}
                          maxLength={50}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>
                              {questionnaireHeaderCharCount}/50
                            </span>
                          }
                          onChange={e => setQuestionnaireHeaderCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireSubHeader"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.questionnaireSubHeader')}
                          </span>
                        }
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.questionnaireSubHeader')}
                          style={{ borderRadius: 6 }}
                          maxLength={120}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>
                              {questionnaireSubHeaderCharCount}/120
                            </span>
                          }
                          onChange={e => setQuestionnaireSubHeaderCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireHeaderColor"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.questionnaireHeaderColor')}
                          </span>
                        }
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.questionnaireHeaderColor')}
                          style={{ borderRadius: 6 }}
                          maxLength={9}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>
                              {questionnaireHeaderColorCharCount}/9
                            </span>
                          }
                          onChange={e => setQuestionnaireHeaderColorCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireSubHeaderColor"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.questionnaireSubHeaderColor')}
                          </span>
                        }
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.questionnaireSubHeaderColor')}
                          style={{ borderRadius: 6 }}
                          maxLength={9}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>
                              {questionnaireSubHeaderColorCharCount}/9
                            </span>
                          }
                          onChange={e => setQuestionnaireSubHeaderColorCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireThankYouHeader"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.questionnaireThankYouHeader')}
                          </span>
                        }
                        rules={[
                          { required: true, message: t('questionSetModal.validation.questionnaireThankYouHeader') },
                        ]}
                      >
                        <Input
                          placeholder={t('questionSetModal.placeholders.questionnaireThankYouHeader')}
                          style={{ borderRadius: 6 }}
                          maxLength={50}
                          suffix={
                            <span style={{ color: '#bfbfbf', fontSize: '14px' }}>{thankYouHeaderCharCount}/50</span>
                          }
                          onChange={e => setThankYouHeaderCharCount(e.target.value.length)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="questionnaireThankYouMessage"
                        label={
                          <span style={{ fontWeight: 'bold' }}>
                            {t('questionSetModal.labels.questionnaireThankYouMessage')}
                          </span>
                        }
                        rules={[
                          { required: true, message: t('questionSetModal.validation.questionnaireThankYouMessage') },
                        ]}
                      >
                        <TextArea
                          placeholder={t('questionSetModal.placeholders.questionnaireThankYouMessage')}
                          autoSize={{ minRows: 3, maxRows: 6 }}
                          style={{ borderRadius: 6 }}
                          maxLength={250}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              <StyledFooter>
                <Space size={12}>
                  {!entryExpanded && (
                    <Button onClick={() => setEntryExpanded(true)} style={{ borderColor: '#222', borderRadius: 4 }}>
                      {t('questionSetModal.buttons.customizeEntryCard')}
                    </Button>
                  )}
                  {!questionnaireExpanded && (
                    <Button
                      onClick={() => setQuestionnaireExpanded(true)}
                      style={{ borderColor: '#222', borderRadius: 4 }}
                    >
                      {t('questionSetModal.buttons.customizeQuestionnaire')}
                    </Button>
                  )}
                </Space>
                <Button type="primary" loading={loading} onClick={handleSubmit} style={{ borderRadius: 4 }}>
                  {questionSetId
                    ? t('questionSetModal.buttons.saveQuestionSet')
                    : t('questionSetModal.buttons.addQuestionSet')}
                </Button>
              </StyledFooter>
            </Form>
          </Spin>
        </StyledFormWrapper>
      </Modal>
    </StyledModalWrapper>
  );
};

const StyledModalWrapper = styled.div`
  .ant-modal-content {
    border-radius: 12px;
  }

  .ant-modal-header {
    padding: 24px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-modal-body {
    padding: 24px;
  }

  .ant-form-item-label > label {
    text-align: left;
    font-weight: 700;
    font-size: 14px;
  }

  .ant-input,
  .ant-select-selector,
  .ant-input-number,
  .ant-picker {
    border-radius: 8px !important;
  }

  .ant-divider-horizontal {
    margin: 16px 0;
  }
`;

const StyledFormWrapper = styled.div`
  padding: 24px;
`;

const StyledSectionTitle = styled.div`
  font-weight: 400;
  font-size: 20px;
  margin: 16px 0 12px;
  color: #000;
`;

const StyledCancelButton = styled(Button)`
  && {
    text-decoration: underline !important;
    padding: 0 !important;
    color: #000 !important;
    font-weight: 700 !important;
  }

  &&:hover,
  &&:focus,
  &&:active {
    color: #333 !important;
    text-decoration: underline !important;
  }

  span {
    text-decoration: underline !important;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;

  button {
    height: 40px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 600;
  }

  .ant-btn-primary {
    background-color: #000;
    border-color: #000;
    text-transform: uppercase;

    &:hover {
      background-color: #333;
      border-color: #333;
    }
  }

  .ant-btn-default {
    border: 1px solid #d9d9d9;

    &:hover {
      border-color: #000;
      color: #000;
    }
  }
`;
